// React hook imports
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

// Custom hook imports
import { useNavigateWithSameKey } from "../../../../../../../custom-hooks/navigateWithKey";

// Redux imports
import { useDispatch, useSelector } from "react-redux";
import { createTestCasesUploadedData, updateTestCasesuploadedState } from "../../../../../../../store/slices/test-automation/web/flow-stages/testcaseUploadSlice";

// MUI component and icon imports
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid2,
  Container,
  Button,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Description as FileIcon,
} from "@mui/icons-material";

// Local images imports
import { ReactComponent as UploadBlueIcon } from "../../../../../../../images/test-automation/uploadIcon_bluecolour.svg";
import { ReactComponent as UploadWhiteIcon } from "../../../../../../../images/test-automation/uploadIcon_whitecolour.svg";
import { ReactComponent as ImportBlueIcon } from "../../../../../../../images/test-automation/importIcon_bluecolour.svg";
import { ReactComponent as ImportWhiteIcon } from "../../../../../../../images/test-automation/importIcon_whitecolour.svg";
import { ReactComponent as NoDataIcon } from "../../../../../../../images/test-automation/Document_empty.svg";
import { ReactComponent as JiraIcon } from "../../../../../../../images/test-automation/Jira_icon.svg";
import { ReactComponent as GitHubIcon } from "../../../../../../../images/test-automation/GitHub_Icon.svg";
import { ReactComponent as SlackIcon } from "../../../../../../../images/test-automation/slack_icon.svg";
import { ReactComponent as ConfluenceIcon } from "../../../../../../../images/test-automation/confluence_icon.svg";
import { ReactComponent as TestRailIcon } from "../../../../../../../images/test-automation/testRail_icon.svg";
import { ReactComponent as QTestIcon } from "../../../../../../../images/test-automation/qTest_Icon.svg";
import { ReactComponent as AzureIcon } from "../../../../../../../images/test-automation/azure_icon.svg";

// Project components imports
import CommonTable from "../../../../../../custom-components/CustomTable";

// Project utility imports
import {
  readExcelFile,
  validateFileFormat,
} from "../../../../../../../utils/fileReader";

// Main function which returns React component
const TestCasesPage = () => {
  const navigate = useNavigateWithSameKey();
  const location = useLocation();
  const dispatch = useDispatch();

  // Capture automationType and locationKey from state
  const { automationType, locationKey } = location.state || {};

  // Capture data from redux store using selector
  const preferences = useSelector((state) =>
    state["testAutomation"][locationKey]["webAutomation"][
      "automationPreferences"
    ] || {}
  );

  const testCases = useSelector((state) =>
    state["testAutomation"][locationKey]["webAutomation"][
      "testCasesUploaded"
    ] || null
  );

  // Define local state variables
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isImportDrawerOpen, setIsImportDrawerOpen] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState("");
  // Modified: Changed to store single selected test case ID instead of array
  const [selectedTestCase, setSelectedTestCase] = useState(null);
  const [pendingExcelData, setPendingExcelData] = useState(null);
  const [isTestCaseDetailsOpen, setIsTestCaseDetailsOpen] = useState(false);
  const [selectedTestCaseForDetails, setSelectedTestCaseForDetails] =
    useState(null);

  // Function to be run when locationKey and testCases are updated
  useEffect(() => {
    if (!locationKey) {
      navigate("/landingpage");
      return;
    }

    if (!testCases) {
      dispatch(createTestCasesUploadedData(locationKey));
    }
  }, [locationKey, testCases])

  const tableColumns = [
    {
      key: "testCaseId",
      label: "Test Case ID",
      maxWidth: "100px",
    },
    {
      key: "title",
      label: "Test Case Title" || "Title",
      type: "link",
      maxWidth: "180px",
      onClick: (row) => handleTestCaseClick(row),
    },
    {
      key: "testSteps",
      label: "Test Steps",
      maxWidth: "200px",
    },
    {
      key: "expectedResult",
      label: "Expected Result",
      maxWidth: "180px",
    },
    {
      key: "moduleName",
      label: "Module Name",
      maxWidth: "120px",
    },
    {
      key: "priority",
      label: "Priority",
      maxWidth: "80px",
    },
    {
      key: "remarks",
      label: "Remarks",
      maxWidth: "150px",
    },
  ];

  // Available applications for import
  const applications = [
    { id: "jira", name: "JIRA", icon: <JiraIcon /> },
    { id: "github", name: "GitHub", icon: <GitHubIcon /> },
    { id: "slack", name: "Slack", icon: <SlackIcon /> },
    { id: "confluence", name: "Confluence", icon: <ConfluenceIcon /> },
    { id: "testrail", name: "TestRail", icon: <TestRailIcon /> },
    { id: "qtest", name: "qTest", icon: <QTestIcon /> },
    { id: "azure", name: "Azure", icon: <AzureIcon /> },
  ];

  // Handle function when upload button clicked or open upload drawer
  const handleUpload = () => {
    setIsDrawerOpen(true);
  };

  // Handle function to close upload drawer
  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
  };

  // Handle function when upload button clicked
  const handleFileUpload = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".xlsx,.xls,.csv";
    input.multiple = false;
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        handleExcelFileUpload(file);
      }
    };
    input.click();
  };

  // Handle function for excel file upload
  const handleExcelFileUpload = async (file) => {
    try {
      // Validate file format
      const validation = validateFileFormat(file);
      if (!validation.isValid) {
        return;
      }

      // Read and parse the Excel file
      const excelData = await readExcelFile(file);

      // Store the data temporarily (don't update test cases yet)
      setPendingExcelData(excelData);

      // Add file to uploaded files list
      const newFile = {
        id: Date.now(),
        name: file.name,
        size: formatFileSize(file.size),
        uploadedAt: "Just now",
        file: file,
      };
      setUploadedFiles((prev) => [...prev, newFile]);

      console.log("Excel data loaded and stored temporarily:", excelData);
      console.log("Data will be imported when Import button is clicked");
    } catch (error) {
      console.error("Error reading Excel file:", error);
    }
  };

  // Handle function when multiple files uploaded
  const addFiles = (files) => {
    const newFiles = files.map((file, index) => ({
      id: Date.now() + index,
      name: file.name,
      size: formatFileSize(file.size),
      uploadedAt: "Just now",
      file: file,
    }));
    setUploadedFiles((prev) => [...prev, ...newFiles]);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Handle function to remove uploaded file
  const handleDeleteFile = (fileId) => {
    setUploadedFiles((prev) => prev.filter((file) => file.id !== fileId));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);

    // Handle Excel files
    const excelFiles = files.filter((file) => {
      const validation = validateFileFormat(file);
      return validation.isValid;
    });

    if (excelFiles.length > 0) {
      // Process the first Excel file
      handleExcelFileUpload(excelFiles[0]);
    } else {
      // Handle other files
      addFiles(files);
    }
  };

  const handleImportFiles = () => {
    if (pendingExcelData && pendingExcelData.length > 0) {
      // Import the pending Excel data
      dispatch(updateTestCasesuploadedState(locationKey, pendingExcelData));
      // setTestCases(pendingExcelData);
      console.log("Importing Excel data:", pendingExcelData);
      console.log("Test cases updated, count:", pendingExcelData.length);

      // Clear pending data
      setPendingExcelData(null);
    }
    setIsDrawerOpen(false);
  };

  const handleImport = () => {
    setIsImportDrawerOpen(true);
  };

  const handleCloseImportDrawer = () => {
    setIsImportDrawerOpen(false);
    setSelectedApplication("");
  };

  const handleApplicationSelect = (appId) => {
    setSelectedApplication(appId);
  };

  const handleImportNext = () => {
    if (selectedApplication) {
      // TODO: Handle import from selected application
      console.log("Importing from:", selectedApplication);
      setIsImportDrawerOpen(false);
      setSelectedApplication("");
    }
  };

  // Modified: Handle single test case selection
  const handleTestCaseSelect = (testCaseId, isSelected) => {
    if (isSelected) {
      setSelectedTestCase(testCaseId);
    } else {
      setSelectedTestCase(null);
    }
  };

  // Modified: Handle select all - now selects/deselects the first test case
  const handleSelectAllTestCases = (isSelected) => {
    if (isSelected && testCases.length > 0) {
      setSelectedTestCase(testCases[0].id);
    } else {
      setSelectedTestCase(null);
    }
  };

  const handleTableSearch = (searchTerm) => {
    // TODO: Implement search functionality
    console.log("Searching for:", searchTerm);
  };

  const handleTableSort = (columnKey, direction) => {
    // TODO: Implement sort functionality
    console.log("Sorting by:", columnKey, direction);
  };

  const handleTestCaseClick = (testCase) => {
    setSelectedTestCaseForDetails(testCase);
    setIsTestCaseDetailsOpen(true);
  };

  const handleCloseTestCaseDetails = () => {
    setIsTestCaseDetailsOpen(false);
    setSelectedTestCaseForDetails(null);
  };

  // Modified: Handle navigation with single selected test case
  const handleNext = () => {
    // Find the selected test case and its index
    const selectedTestCaseData = testCases.find(
      (tc) => tc.id === selectedTestCase
    );
    const selectedTestCaseIndex = testCases.findIndex(
      (tc) => tc.id === selectedTestCase
    );

    if (!selectedTestCaseData) {
      console.error("No test case selected");
      return;
    }

    const automationPath = `/dashboard/test-automation/${
      preferences.automationTool
    }${`-${preferences.programmingLanguage}`}${`-${preferences.framework}`}/${
      preferences.testCaseType + preferences.projectType
    }`;

    navigate(`${automationPath}/test-case-imported`, {
        automationType,
        automationPath,
        testCases: [selectedTestCaseData]
      }
    );
  };

  const handleBack = () => {
    navigate(-1);
  };

  const renderUploadCard = (
    title,
    description,
    buttonText,
    blueIcon,
    whiteIcon,
    onClick,
    disabled
  ) => (
    <Card
      sx={{
        cursor: "pointer",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        "&:hover": {
          borderColor: "#0094FB",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        },
        "&:active": {
          backgroundColor: "#E1F5FF",
          borderColor: "#0094FB",
          borderWidth: "2px",
        },
        backgroundColor: "white",
        width: "540px",
        height: "200px",
        margin: "0 auto",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        opacity: 1,
      }}
      onClick={disabled ? () => {} : onClick}
    >
      <CardContent
        sx={{
          p: "20px",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "center",
          textAlign: "center",
          gap: "16px",
          pt: "12px",
        }}
      >
        {/* Top Section - Icon beside Title, Description below */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          {/* Icon and Title in same row */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              {blueIcon}
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 500,
                fontStyle: "Medium",
                fontSize: "16px",
                color: "#000000",
                textAlign: "center",
              }}
            >
              {title}
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{ color: "#666", textAlign: "center" }}
          >
            {description}
          </Typography>
        </Box>

        {/* Bottom Section - Button */}
        <Button
          variant="contained"
          startIcon={disabled ? blueIcon : whiteIcon}
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
          sx={{
            backgroundColor: "#0094FB",
            "&:hover": {
              backgroundColor: "#0094FB",
            },
            width: "178px",
            height: "46px",
            borderRadius: "8px",
            fontWeight: "bold",
            fontSize: "14px",
            padding: "10px 24px 10px 16px",
            gap: "8px",
            border: disabled ? "" : "2px solid #0094FB",
            opacity: 1,
          }}
        >
          {buttonText}
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "#F6F3F2",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        py: 4,
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* White Content Area */}
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "8px 8px 0 0",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            p: 4,
            flex: 1,
          }}
        >
          {/* Upload/Import Section */}
          <Box sx={{ mb: 4 }}>
            <Grid2
              container
              spacing={3}
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(auto-fit, minmax(500px, 1fr))",
                  md: "repeat(auto-fit, minmax(500px, 1fr))",
                  lg: "repeat(auto-fit, minmax(500px, 1fr))",
                },
                gap: "16px",
                width: "100%",
                maxWidth: "none",
                margin: "0 auto",
              }}
            >
              <Grid2 item xs={6}>
                {renderUploadCard(
                  "Upload Test Cases",
                  "Upload a XLS or XLSX test case file",
                  "Choose File",
                  <UploadBlueIcon />,
                  <UploadWhiteIcon />,
                  handleUpload
                )}
              </Grid2>
              <Grid2 item xs={6}>
                {renderUploadCard(
                  "Enterprise Repositories",
                  "Import test cases from Jira-Xray, Azure, qTest, TestRail",
                  "Import",
                  <ImportBlueIcon />,
                  <ImportWhiteIcon />,
                  handleImport,
                  true
                )}
              </Grid2>
            </Grid2>
          </Box>

          {/* Test Cases Display Section */}
          <Box sx={{ mb: 4 }}>
            {!!testCases && (testCases.length === 0 ? (
              <Box
                sx={{
                  textAlign: "center",
                  py: 8,
                  color: "#666",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                  minHeight: "400px",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 2,
                  }}
                >
                  <NoDataIcon />
                </Box>
                <Typography
                  variant="h6"
                  sx={{ color: "#666", fontWeight: 500 }}
                >
                  No test cases uploaded
                </Typography>
                <Typography variant="body2" sx={{ color: "#999" }}>
                  Upload or import test cases to get started
                </Typography>
              </Box>
            ) : (
              <CommonTable
                data={testCases}
                columns={tableColumns}
                title="Test Cases"
                searchable={true}
                selectable={true}
                // Modified: Pass single selected test case as array for compatibility
                selectedRows={selectedTestCase ? [selectedTestCase] : []}
                onRowSelect={handleTestCaseSelect}
                onSelectAll={handleSelectAllTestCases}
                onSearch={handleTableSearch}
                onSort={handleTableSort}
                searchPlaceholder="Search test cases..."
                rowClickable={true}
                onRowClick={(row) => console.log("Row clicked:", row)}
              />
            ))}
          </Box>
        </Box>

        {/* Footer Area */}
        <Box
          sx={{
            mt: 4,
            backgroundColor: "transparent",
            borderRadius: "0 0 8px 8px",
          }}
        >
          {/* Navigation Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            {/* Left: Back Button */}
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={handleBack}
              sx={{
                // width: "120px",
                // height: "40px",
                borderColor: "#0094FB",
                color: "#0094FB",
                textTransform: "none",
                // fontFamily: "Manrope",
                fontWeight: 500,
                borderRadius: "8px",
                borderWidth: "2px",
                paddingTop: "8px",
                paddingRight: "16px",
                paddingBottom: "8px",
                paddingLeft: "16px",
                gap: "8px",
                "&:hover": {
                  borderColor: "#0094FB",
                  backgroundColor: "rgba(0, 148, 251, 0.04)",
                },
              }}
            >
              Back
            </Button>

            {/* Right: Configure Button */}
            <Button
              variant="contained"
              endIcon={<NextIcon />}
              onClick={handleNext}
              // Modified: Disable button if no test case is selected
              disabled={!selectedTestCase}
              sx={{
                // width: "199px",
                // height: "40px",
                backgroundColor: selectedTestCase ? "#0094FB" : "#ccc",
                color: "white",
                textTransform: "none",
                // fontFamily: "Manrope",
                fontWeight: 500,
                borderRadius: "8px",
                borderWidth: "2px",
                borderColor: selectedTestCase ? "#0094FB" : "#ccc",
                paddingTop: "8px",
                paddingRight: "16px",
                paddingBottom: "8px",
                paddingLeft: "16px",
                // gap: "8px",
                "&:hover": {
                  backgroundColor: selectedTestCase ? "#0078D4" : "#ccc",
                  borderColor: selectedTestCase ? "#0078D4" : "#ccc",
                },
                "&:disabled": {
                  backgroundColor: "#ccc",
                  borderColor: "#ccc",
                  color: "#666",
                },
              }}
            >
              Configure
            </Button>
          </Box>
        </Box>
      </Container>

      {/* Upload Drawer */}
      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={handleCloseDrawer}
        sx={{
          "& .MuiDrawer-paper": {
            width: 400,
            height: "100%",
          },
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Header */}
          <Box
            sx={{
              p: 2,
              borderBottom: "1px solid #e0e0e0",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <BackIcon sx={{ color: "#666" }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: "#333" }}>
                Upload API Specs
              </Typography>
            </Box>
            <IconButton onClick={handleCloseDrawer}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content */}
          <Box
            sx={{
              flex: 1,
              p: 2,
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            {/* Upload Area */}
            <Box
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={handleFileUpload}
              sx={{
                border: isDragOver ? "2px solid #0094FB" : "2px dashed #0094FB",
                borderRadius: "8px",
                p: 4,
                textAlign: "center",
                backgroundColor: isDragOver ? "#E1F5FF" : "#F0F8FF",
                minHeight: "200px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: 2,
                cursor: "pointer",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  backgroundColor: "#E1F5FF",
                  borderColor: "#0078CC",
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <UploadBlueIcon />
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 500, color: "#333" }}
                >
                  Upload Test Cases
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: "#666" }}>
                Drag and drop your Excel file (.xlsx, .xls, .csv) here, or click
                to select
              </Typography>
              <Button
                variant="contained"
                startIcon={<UploadWhiteIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleFileUpload();
                }}
                sx={{
                  backgroundColor: "#0094FB",
                  "&:hover": {
                    backgroundColor: "#0078CC",
                  },
                  mt: 2,
                }}
              >
                Choose File
              </Button>
            </Box>

            {/* Uploaded Files Section */}
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "#333",
                  mb: 2,
                  // fontFamily: "Manrope",
                }}
              >
                Uploaded Files{" "}
                {uploadedFiles.length > 0 && `(${uploadedFiles.length})`}
              </Typography>
              {uploadedFiles.length === 0 ? (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 4,
                    color: "#666",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <FileIcon sx={{ fontSize: 48, color: "#ccc" }} />
                  <Typography variant="body2">No files uploaded yet</Typography>
                </Box>
              ) : (
                <List>
                  {uploadedFiles.map((file, index) => (
                    <React.Fragment key={file.id}>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon>
                          <FileIcon sx={{ color: "#0094FB" }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={file.name}
                          secondary={`${file.size} • ${file.uploadedAt}`}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={() => handleDeleteFile(file.id)}
                            sx={{ color: "#f44336" }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                      {index < uploadedFiles.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Box>
          </Box>

          {/* Footer Buttons */}
          <Box
            sx={{
              p: 2,
              borderTop: "1px solid #e0e0e0",
              display: "flex",
              gap: 2,
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="outlined"
              onClick={handleCloseDrawer}
              sx={{
                width: "120px",
                height: "40px",
                borderRadius: "8px",
                gap: "8px",
                opacity: 1,
                borderWidth: "2px",
                paddingTop: "8px",
                paddingRight: "16px",
                paddingBottom: "8px",
                paddingLeft: "16px",
                borderColor: "#0094FB",
                color: "#0094FB",
                "&:hover": {
                  borderColor: "#0078CC",
                  backgroundColor: "rgba(0, 148, 251, 0.04)",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleImportFiles}
              disabled={!pendingExcelData || pendingExcelData.length === 0}
              sx={{
                width: "120px",
                height: "40px",
                borderRadius: "8px",
                gap: "8px",
                opacity: 1,
                borderWidth: "2px",
                paddingTop: "8px",
                paddingRight: "16px",
                paddingBottom: "8px",
                paddingLeft: "16px",
                backgroundColor:
                  !pendingExcelData || pendingExcelData.length === 0
                    ? "#ccc"
                    : "#0094FB",
                "&:hover": {
                  backgroundColor:
                    !pendingExcelData || pendingExcelData.length === 0
                      ? "#ccc"
                      : "#0078CC",
                },
                "&:disabled": {
                  backgroundColor: "#ccc",
                  color: "#666",
                },
              }}
            >
              Import
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Import Drawer */}
      <Drawer
        anchor="right"
        open={isImportDrawerOpen}
        onClose={handleCloseImportDrawer}
        sx={{
          "& .MuiDrawer-paper": {
            width: 500,
            height: "100%",
          },
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Header */}
          <Box
            sx={{
              p: 2,
              borderBottom: "1px solid #e0e0e0",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <BackIcon sx={{ color: "#666" }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: "#333" }}>
                Select an application
              </Typography>
            </Box>
            <IconButton onClick={handleCloseImportDrawer}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, p: 3 }}>
            <Grid2
              container
              spacing={2}
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(auto-fit, minmax(500px, 1fr))",
                  md: "repeat(auto-fit, minmax(500px, 1fr))",
                  lg: "repeat(auto-fit, minmax(500px, 1fr))",
                },
                gap: "16px",
                width: "100%",
              }}
            >
              {applications.map((app) => (
                <Grid2 item xs={6} key={app.id}>
                  <Card
                    onClick={() => handleApplicationSelect(app.id)}
                    sx={{
                      width: "177px",
                      height: "85px",
                      cursor: "pointer",
                      border:
                        selectedApplication === app.id
                          ? "2px solid #0094FB"
                          : "1px solid #e0e0e0",
                      backgroundColor:
                        selectedApplication === app.id ? "#E1F5FF" : "white",
                      borderRadius: "8px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      justifyContent: "flex-start",
                      gap: 1,
                      padding: "12px",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        backgroundColor:
                          selectedApplication === app.id
                            ? "#E1F5FF"
                            : "#f5f5f5",
                        borderColor: "#0094FB",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                      }}
                    >
                      {app.icon}
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        // fontFamily: "Manrope",
                        fontWeight: 500,
                        fontStyle: "Medium",
                        fontSize: "16px",
                        color: "#000000",
                        textAlign: "left",
                      }}
                    >
                      {app.name}
                    </Typography>
                  </Card>
                </Grid2>
              ))}
            </Grid2>
          </Box>

          {/* Footer Buttons */}
          <Box
            sx={{
              p: 2,
              borderTop: "1px solid #e0e0e0",
              display: "flex",
              gap: 2,
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="outlined"
              onClick={handleCloseImportDrawer}
              sx={{
                width: "120px",
                height: "40px",
                borderRadius: "8px",
                gap: "8px",
                opacity: 1,
                borderWidth: "2px",
                paddingTop: "8px",
                paddingRight: "16px",
                paddingBottom: "8px",
                paddingLeft: "16px",
                borderColor: "#0094FB",
                color: "#0094FB",
                "&:hover": {
                  borderColor: "#0078CC",
                  backgroundColor: "rgba(0, 148, 251, 0.04)",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleImportNext}
              disabled={!selectedApplication}
              sx={{
                width: "120px",
                height: "40px",
                borderRadius: "8px",
                gap: "8px",
                opacity: 1,
                borderWidth: "2px",
                paddingTop: "8px",
                paddingRight: "16px",
                paddingBottom: "8px",
                paddingLeft: "16px",
                backgroundColor: !selectedApplication ? "#ccc" : "#0094FB",
                "&:hover": {
                  backgroundColor: !selectedApplication ? "#ccc" : "#0078CC",
                },
                "&:disabled": {
                  backgroundColor: "#ccc",
                  color: "#666",
                },
              }}
            >
              Next
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Test Case Details Drawer */}
      <Drawer
        anchor="right"
        open={isTestCaseDetailsOpen}
        onClose={handleCloseTestCaseDetails}
        sx={{
          "& .MuiDrawer-paper": {
            width: 600,
            height: "100%",
          },
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Header */}
          <Box
            sx={{
              p: 2,
              borderBottom: "1px solid #e0e0e0",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <BackIcon sx={{ color: "#666" }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: "#333" }}>
                Test Case Details
              </Typography>
            </Box>
            <IconButton onClick={handleCloseTestCaseDetails}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, p: 3 }}>
            {selectedTestCaseForDetails && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {/* First Row - Two Columns */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "1fr 1fr",
                    },
                    gap: 3,
                  }}
                >
                  {/* Left Column */}
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ color: "#666", mb: 0.5 }}
                      >
                        Test Case Title
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedTestCaseForDetails.title}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ color: "#666", mb: 0.5 }}
                      >
                        Module Name
                      </Typography>
                      <Typography
                        variant="body1"
                        // sx={{ fontFamily: "Manrope" }}
                      >
                        {selectedTestCaseForDetails.moduleName || "N/A"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ color: "#666", mb: 0.5 }}
                      >
                        Priority
                      </Typography>
                      <Typography
                        variant="body1"
                        // sx={{ fontFamily: "Manrope" }}
                      >
                        {selectedTestCaseForDetails.priority || "N/A"}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Right Column */}
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ color: "#666", mb: 0.5 }}
                      >
                        Test Case ID
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedTestCaseForDetails.testCaseId}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ color: "#666", mb: 0.5 }}
                      >
                        Remarks
                      </Typography>
                      <Typography
                        variant="body1"
                        // sx={{ fontFamily: "Manrope" }}
                      >
                        {selectedTestCaseForDetails.remarks || "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Second Row - Full Width Sections */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: "#666", mb: 0.5 }}>
                      Test Steps
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
                      {selectedTestCaseForDetails.testSteps ||
                        "No test steps defined"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ color: "#666", mb: 0.5 }}>
                      Expected Result
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
                      {selectedTestCaseForDetails.expectedResult ||
                        "No expected result provided"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>

          {/* Footer Buttons */}
          <Box
            sx={{
              mt: 4,
              borderTop: "1px solid #e0e0e0",
              display: "flex",
              gap: 2,
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="outlined"
              onClick={handleCloseTestCaseDetails}
              startIcon={<BackIcon />}
              sx={{
                // width: "120px",
                // height: "40px",
                borderRadius: "8px",
                gap: "8px",
                opacity: 1,
                borderWidth: "2px",
                paddingTop: "8px",
                paddingRight: "16px",
                paddingBottom: "8px",
                paddingLeft: "16px",
                borderColor: "#0094FB",
                color: "#0094FB",
                "&:hover": {
                  borderColor: "#0078CC",
                  backgroundColor: "rgba(0, 148, 251, 0.04)",
                },
              }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleCloseTestCaseDetails}
              sx={{
                // width: "120px",
                // height: "40px",
                borderRadius: "8px",
                gap: "8px",
                opacity: 1,
                borderWidth: "2px",
                paddingTop: "8px",
                paddingRight: "16px",
                paddingBottom: "8px",
                paddingLeft: "16px",
                backgroundColor: "#0094FB",
                "&:hover": {
                  backgroundColor: "#0078CC",
                },
              }}
            >
              OK
            </Button>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default TestCasesPage;
